import React from 'react';
import type { VisionLabelSummary, VisionPackSummary } from './api';

export const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v * 100)}%`);
const ms = (v: number | null | undefined) => (v == null ? '—' : `${(v / 1000).toFixed(1)} s`);

/** Per pack: a candidate beside the live model, or the live model alone. */
export function PackTable({ candidate, production }: { candidate?: Record<string, VisionPackSummary>; production: Record<string, VisionPackSummary> }) {
  const packs = [...new Set([...Object.keys(candidate ?? {}), ...Object.keys(production)])].sort();
  const cell = (c: VisionPackSummary | undefined, l: VisionPackSummary | undefined, f: (s: VisionPackSummary | undefined) => string) =>
    candidate ? `${f(c)} / ${f(l)}` : f(l);
  return (
    <table className="matrix" style={{ width: '100%' }}>
      <thead>
        <tr>
          <th>Pack{candidate ? ' (new / live)' : ''}</th>
          <th className="col-ed">Scenarios</th>
          <th className="col-ed">Accuracy</th>
          <th className="col-ed">Violations</th>
          <th className="col-ed">Category recall</th>
          <th className="col-ed">False positives</th>
          <th className="col-ed">p50 / p95</th>
        </tr>
      </thead>
      <tbody>
        {packs.map((p) => {
          const c = candidate?.[p];
          const l = production[p];
          return (
            <tr key={p}>
              <td>{p}</td>
              <td className="col-ed">{cell(c, l, (s) => String(s?.scenarios ?? '—'))}</td>
              <td className="col-ed">{cell(c, l, (s) => pct(s?.accuracy))}</td>
              <td className="col-ed">{cell(c, l, (s) => String(s?.violations ?? '—'))}</td>
              <td className="col-ed">{cell(c, l, (s) => pct(s?.categoryRecall))}</td>
              <td className="col-ed">{cell(c, l, (s) => String(s?.falsePositives ?? '—'))}</td>
              <td className="col-ed">{cell(c, l, (s) => `${ms(s?.p50ms)} / ${ms(s?.p95ms)}`)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const interval = (i: { low: number; high: number } | null) => (i ? `${Math.round(i.low * 100)}–${Math.round(i.high * 100)}%` : '—');
const gateMark = (v: boolean | null) => (v == null ? '—' : v ? 'yes' : 'no');

/**
 * Per label, per pack: the numbers the release gate reads (SPECIALIST_MODEL_READINESS.md,
 * "What a release needs") and whether this set can support them at all.
 */
export function LabelTable({ packs }: { packs: Record<string, VisionPackSummary> }) {
  const rows = Object.entries(packs)
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([pack, s]) => Object.entries(s.labels ?? {}).map(([label, l]) => ({ pack, label, l })));
  if (rows.length === 0) return <p className="muted">No labelled defect categories in this set.</p>;
  return (
    <>
      <p className="sub" style={{ marginBottom: 8 }}>
        <strong>Precision</strong> = of the photos where the model claimed the label, how many really showed it (what a wrong alarm costs the inspector).{' '}
        <strong>Recall</strong> = of the photos that showed it, how many the model found (what a miss costs). The recall range is the 95% interval the count
        can honestly support. A label needs {LABEL_GATE.minPositives} photos showing it and {LABEL_GATE.minNegatives} that do not, precision ≥{' '}
        {pct(LABEL_GATE.minPrecision)} and recall ≥ {pct(LABEL_GATE.minRecall)} before its number means anything for a release.
      </p>
      <div className="matrix-scroll">
        <table className="matrix" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Pack</th>
              <th>Label</th>
              <th className="col-ed">Photos with / without</th>
              <th className="col-ed">Precision</th>
              <th className="col-ed">Recall (95%)</th>
              <th className="col-ed">F1</th>
              <th className="col-ed">FP / FN</th>
              <th className="col-ed">Enough examples</th>
              <th className="col-ed">Meets gate</th>
              <th>Reading</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ pack, label, l }) => (
              <LabelRow key={`${pack}:${label}`} pack={pack} label={label} l={l} />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const LABEL_GATE = { minPositives: 50, minNegatives: 200, minPrecision: 0.85, minRecall: 0.7 };

function LabelRow({ pack, label, l }: { pack: string; label: string; l: VisionLabelSummary }) {
  return (
    <tr style={l.enoughExamples ? undefined : { opacity: 0.75 }}>
      <td>{pack}</td>
      <td>{label}</td>
      <td className="col-ed">{l.positives} / {l.negatives}</td>
      <td className="col-ed">{pct(l.precision)}</td>
      <td className="col-ed">
        {pct(l.recall)}
        <div className="muted" style={{ fontSize: 11 }}>{interval(l.recallInterval)}</div>
      </td>
      <td className="col-ed">{l.f1 == null ? '—' : l.f1.toFixed(2)}</td>
      <td className="col-ed">{l.fp} / {l.fn}</td>
      <td className="col-ed">{gateMark(l.enoughExamples)}</td>
      <td className="col-ed">{gateMark(l.pass)}</td>
      <td className="muted" style={{ fontSize: 12, minWidth: 280, maxWidth: 420, whiteSpace: 'normal' }}>{l.note}</td>
    </tr>
  );
}
